import { defineStore } from 'pinia';
import request from '@/utils/request';
import Cookies from 'js-cookie';
import { ElMessage } from 'element-plus';

interface VideoItem {
    id: string | number;
    title?: string;
    imagePath?: string;
    pdf_path?: string;
    author?: string;
    department?: string;
    publishDate?: string;
    views?: number;
    [key: string]: any; // 允许添加其他属性
}

export const useVideoStore = defineStore('Video', {
    state: () => ({
        // 视频列表
        VideoList: [] as VideoItem[],
        // 当前选中的视频
        currentVideo: null as VideoItem | null,
        // 是否获取视频列表
        isGetVideoList: false as boolean,
        // 加载状态
        loading: false,
        // 错误信息
        error: null as string | null,
    }),

    getters: {
        // 获取视频列表
        getVideoList: state => state.VideoList,
        // 获取当前视频
        getCurrentVideo: state => state.currentVideo,
        // 通过ID查找视频
        getVideoById: state => (id: string | number) => {
            return state.VideoList.find(Video => Video.id === id) || null;
        },
    },

    actions: {
        // 设置当前视频
        setCurrentVideo(Video: VideoItem) {
            this.currentVideo = Video;
        },
        // 设置视频列表
        setVideoList(VideoList: VideoItem[]) {
            this.VideoList = VideoList;
        },
        // 设置是否获取视频列表
        setIsGetVideoList(isGetVideoList: boolean) {
            this.isGetVideoList = isGetVideoList;
        },

        // 从后端获取视频列表
        async fetchVideoList(
            pageSize: any = 12,
            currentPage: any = 1,
            categoryId: string = 'all',
            timeSort: string = 'desc'
        ) {
            this.loading = true;
            this.error = null;

            try {
                // 构建URL，如果categoryId不是'all'，则添加分类过滤条件
                let url = `/api/resources/video?currentPage=${currentPage}&pageSize=${pageSize}`;
                if (categoryId !== 'all') {
                    url += `&categoryId=${categoryId}`;
                }
                if (timeSort !== 'desc') {
                    url += `&order=${timeSort}`;
                }
                const res: any = await request({
                    url: url, // 确保URL路径正确
                    method: 'get',
                });
                // console.log(res);
                if (res.code === 200) {
                    return res.data.result;
                } else {
                    ElMessage.error('获取视频列表失败');
                }
            } catch (error: any) {
                ElMessage.error('获取视频列表失败');
            }
        },

        // 获取单个视频详情
        async fetchVideoDetail(id: string | number) {
            this.loading = true;
            this.error = null;
            try {
                // 先查看是否在本地已有该视频数据
                const existingVideo = this.getVideoById(id);
                if (existingVideo) {
                    this.currentVideo = existingVideo;
                    return;
                }

                // 如果没有，则从后端获取
                const res = await request({
                    url: `/api/resources/ppt/${id}`,
                    method: 'get',
                });

                if (res.data && res.data.result) {
                    this.currentVideo = res.data.result;

                    // 将获取到的视频添加到列表中（如果不存在）
                    if (!this.VideoList.some(Video => Video.id === id)) {
                        this.VideoList.push(res.data.result);
                    }
                }
            } catch (error: any) {
                console.error('获取视频详情失败:', error);
                this.error = error.message || '获取视频详情失败';
            } finally {
                this.loading = false;
            }
        },
        // 删除视频
        async deleteVideo(id: string | number) {
            try {
                const res: any = await request({
                    url: `/api/resources/video/${id}`,
                    method: 'delete',
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`,
                    },
                });
                // console.log('deleteVideo', res);
                if (res.code === 200 && res.message === '视频删除成功。') {
                    ElMessage.success('删除视频成功');
                } else {
                    ElMessage.error('删除视频失败');
                }
            } catch (error) {
                // console.error('删除视频失败:', error);
                ElMessage({
                    type: 'error',
                    message: '删除视频失败',
                });
            }
        },

        // 清除当前视频数据
        clearCurrentVideo() {
            this.currentVideo = null;
        },
    },
    persist: true,
});
